import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Rocket, Gamepad2, MessageSquare, Palette, Clock, Users, Trophy, ChevronDown, ChevronUp, Star } from 'lucide-react';
export const KidsAIProgram = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [hoveredModule, setHoveredModule] = useState<number | null>(null);
  const projects = [{
    icon: <Gamepad2 size={48} data-id="element-3645" data-mp="37x0zjzhtql2a" />,
    title: 'Build Cool Games',
    description: 'Create your own video games with AI characters that learn and adapt!',
    color: '#FF6B6B'
  }, {
    icon: <MessageSquare size={48} data-id="element-3646" data-mp="2xzd3954kl5i0" />,
    title: 'Smart Chatbots',
    description: 'Design chatbots that can talk, joke, and help with homework!',
    color: '#4ECDC4'
  }, {
    icon: <Palette size={48} data-id="element-3647" data-mp="32vv5nycfgrr6" />,
    title: 'AI Art & Music',
    description: 'Make amazing art and music using AI tools - no drawing skills needed!',
    color: '#FFE66D'
  }];
  const weeklyProgram = [{
    week: 1,
    title: 'Meet Your AI Friend',
    activities: ['What is AI?', 'Talk to your first chatbot', 'Build a simple AI helper'],
    color: '#667EEA'
  }, {
    week: 2,
    title: 'Create & Play',
    activities: ['Design AI games', 'Make characters that learn', 'Share with friends'],
    color: '#F093FB'
  }, {
    week: 3,
    title: 'Art & Imagination',
    activities: ['AI drawing tools', 'Music creation', 'Story generators'],
    color: '#4FACFE'
  }, {
    week: 4,
    title: 'Show & Tell',
    activities: ['Finish your project', 'Present to parents', 'Get your certificate!'],
    color: '#43E97B'
  }];
  const testimonials = [{
    name: 'Emma, Age 10',
    quote: 'I made a game where the robot learns to jump higher! My friends love it!',
    image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=400&q=80',
    project: 'AI Jump Game'
  }, {
    name: 'Marcus, Age 12',
    quote: "I built a chatbot that helps me with my math homework. It's like having a smart friend!",
    image: 'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=400&q=80',
    project: 'Math Helper Bot'
  }, {
    name: 'Zara, Age 9',
    quote: 'The AI made the coolest drawings for my story! I want to be an AI artist now!',
    image: 'https://images.unsplash.com/photo-1534982841079-afde227ada8f?w=400&q=80',
    project: 'AI Story Illustrator'
  }];
  const faqs = [{
    question: 'Does my child need coding experience?',
    answer: "Nope! We start from scratch and make it super fun and easy. If they can use a tablet or computer, they're ready!"
  }, {
    question: 'What equipment do they need?',
    answer: 'Just a computer or tablet with internet. We provide all the AI tools and software - everything is online and easy to use!'
  }, {
    question: 'Is it safe for kids?',
    answer: 'Absolutely! We use kid-safe AI tools, have live instructors monitoring, and all activities are age-appropriate. Plus, we never share personal information.'
  }, {
    question: 'What if they get stuck?',
    answer: 'Our friendly instructors are always there to help! Plus, kids work in small groups so they can help each other too.'
  }, {
    question: 'Can they continue after the 4 weeks?',
    answer: 'Yes! We have advanced programs, clubs, and monthly challenges to keep the learning going!'
  }];
  return <div className="min-h-screen" style={{
    backgroundColor: '#F0F4FF'
  }} data-id="element-3648" data-mp="3uq71ff4vrcwa">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{
      background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)'
    }} data-id="element-3649" data-mp="3qov74wb2a3hr">
        {/* Floating Elements */}
        <motion.div className="absolute top-20 left-10 text-6xl" animate={{
        y: [0, -20, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity
      }} data-id="element-3650" data-mp="3gp4b2q6urgfk">
          🤖
        </motion.div>
        <motion.div className="absolute top-40 right-20 text-5xl" animate={{
        y: [0, 20, 0],
        rotate: [0, 10, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity
      }} data-id="element-3651" data-mp="1eyj6fp9ffug4">
          ✨
        </motion.div>
        <motion.div className="absolute bottom-20 left-1/4 text-5xl" animate={{
        y: [0, -15, 0]
      }} transition={{
        duration: 3.5,
        repeat: Infinity
      }} data-id="element-3652" data-mp="1i0nkjirtb8fn">
          🚀
        </motion.div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-3653" data-mp="1842eqv7yxtar">
          <div className="max-w-4xl mx-auto text-center" data-id="element-3654" data-mp="1cz80nu1df7yu">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            duration: 0.5,
            type: 'spring'
          }} className="inline-block mb-6" data-id="element-3655" data-mp="z8utvv07tzb3">
              <div className="bg-yellow-300 text-purple-900 px-6 py-2 rounded-full font-bold text-lg" data-id="element-3656" data-mp="pc9r7gbo50af">
                🎉 Ages 8-16 • 4-Week Program
              </div>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-5xl md:text-7xl font-black mb-6 text-white" style={{
            textShadow: '4px 4px 0px rgba(0,0,0,0.2)'
          }} data-id="element-3657" data-mp="gblagduw4ltu">
              Build Amazing Things with AI! 🌟
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="text-2xl md:text-3xl mb-8 text-white font-bold" data-id="element-3658" data-mp="apb1pqwdgfyc">
              Create games, chatbots, and art using the power of AI!
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.6
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3659" data-mp="terbl4ty5krf">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="px-10 py-5 rounded-full font-black text-2xl shadow-2xl" style={{
              backgroundColor: '#FFE66D',
              color: '#764BA2'
            }} data-id="element-3660" data-mp="13audqaostjed">
                🚀 Enroll Now!
              </motion.button>
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="px-10 py-5 rounded-full font-bold text-xl border-4 border-white text-white" data-id="element-3661" data-mp="50kr4qjqrm9o">
                Watch Demo Video
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Hero Image Section */}
        <div className="container mx-auto px-4 mt-12" data-id="element-3662" data-mp="nrlya1v0mo4b">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="max-w-5xl mx-auto" data-id="element-3663" data-mp="nprg2vb4pa3x">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white" data-id="element-3664" data-mp="2uibkkw535avk">
              <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=80" alt="Kids learning AI" className="w-full h-96 object-cover" data-id="element-3665" data-mp="2myivlsueizvn" />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent" data-id="element-3666" data-mp="2i6re07t844hn" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Build Section */}
      <section className="py-20" style={{
      backgroundColor: '#FFFFFF'
    }} data-id="element-3667" data-mp="287ti2jtgx8om">
        <div className="container mx-auto px-4" data-id="element-3668" data-mp="35x4z1ede532q">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3669" data-mp="30ytqd6yu0ym3">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#764BA2'
          }} data-id="element-3670" data-mp="2rwu522gsb8dc">
              What You'll Build 🎨
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3671" data-mp="1giyqautsl1mn">
              Amazing projects that you can show your friends and family!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3672" data-mp="3fm5ldfx7kxki">
            {projects.map((project, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} whileHover={{
            scale: 1.05,
            rotate: 2
          }} className="relative p-8 rounded-3xl shadow-xl cursor-pointer" style={{
            backgroundColor: project.color
          }} onMouseEnter={() => setHoveredModule(index)} onMouseLeave={() => setHoveredModule(null)} data-id="element-3673" data-mp="3kkgsvk016vzk" data-mpi={index}>
                <motion.div animate={{
              rotate: hoveredModule === index ? [0, -10, 10, 0] : 0
            }} transition={{
              duration: 0.5
            }} className="text-white mb-4" data-id="element-3674" data-mp="3ujg49ac6l5q9">
                  {project.icon}
                </motion.div>
                <h3 className="text-3xl font-black mb-3 text-white" data-id="element-3675" data-mp="109y0mh6892kz">
                  {project.title}
                </h3>
                <p className="text-xl text-white font-medium" data-id="element-3676" data-mp="w832tmni97by">
                  {project.description}
                </p>
                {hoveredModule === index && <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="absolute -top-4 -right-4 bg-yellow-300 rounded-full p-4 shadow-lg" data-id="element-3677" data-mp="1516cdjv1501b">
                    <Sparkles size={32} color="#764BA2" data-id="element-3678" data-mp="da9yqh15arcz" />
                  </motion.div>}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How It Works - 4 Week Program */}
      <section className="py-20" style={{
      backgroundColor: '#F0F4FF'
    }} data-id="element-3679" data-mp="maycrbqjgrpv">
        <div className="container mx-auto px-4" data-id="element-3680" data-mp="md1ayj1uhr5o">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3681" data-mp="22nn84ko475ky">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#667EEA'
          }} data-id="element-3682" data-mp="2sbn4t9zf3z6k">
              Your 4-Week Adventure 🗓️
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3683" data-mp="2ilk4sptvpkyq">
              Each week is packed with fun projects and new skills!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-id="element-3684" data-mp="29lxd4vqybtiv">
            {weeklyProgram.map((week, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15
          }} whileHover={{
            y: -10
          }} className="relative p-6 rounded-3xl shadow-xl bg-white border-4" style={{
            borderColor: week.color
          }} data-id="element-3685" data-mp="24mkqab02ymga" data-mpi={index}>
                <div className="absolute -top-6 left-6 w-12 h-12 rounded-full flex items-center justify-center text-2xl font-black text-white" style={{
              backgroundColor: week.color
            }} data-id="element-3686" data-mp="3p0l0qviwm35b">
                  {week.week}
                </div>
                <h3 className="text-2xl font-black mb-4 mt-4" style={{
              color: week.color
            }} data-id="element-3687" data-mp="a7ncx30dcszw">
                  {week.title}
                </h3>
                <ul className="space-y-2" data-id="element-3688" data-mp="3el3r73f5woox">
                  {week.activities.map((activity, i) => <li key={i} className="flex items-start gap-2 text-gray-700" data-id="element-3689" data-mp="3imfgt8y1d9x4" data-mpi={i}>
                      <span className="text-xl" data-id="element-3690" data-mp="3ioi61qrd6z0s">✓</span>
                      <span className="font-medium" data-id="element-3691" data-mp="3nkgypqjq7370">{activity}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Build Your First AI Project Demo */}
      <section className="py-20" style={{
      background: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)'
    }} data-id="element-3692" data-mp="12v1drc76u8pa">
        <div className="container mx-auto px-4" data-id="element-3693" data-mp="17w79liw5ac31">
          <div className="max-w-5xl mx-auto" data-id="element-3694" data-mp="i57a0uywncwd">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-12 shadow-2xl" data-id="element-3695" data-mp="i81toka3gtoq">
              <div className="text-center mb-8" data-id="element-3696" data-mp="n5uxj6nxdblu">
                <h2 className="text-5xl font-black mb-4" style={{
                color: '#667EEA'
              }} data-id="element-3697" data-mp="3vkvf36pjh1vb">
                  Try It Right Now! 🎮
                </h2>
                <p className="text-2xl text-gray-600" data-id="element-3698" data-mp="3qk7djr3op2mu">
                  Click the robot to see AI in action!
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl p-8 min-h-96 flex items-center justify-center" data-id="element-3699" data-mp="4dsnie5sgtkk">
                <motion.div whileHover={{
                scale: 1.1,
                rotate: 5
              }} whileTap={{
                scale: 0.9
              }} className="cursor-pointer text-center" data-id="element-3700" data-mp="3csj2zs932m7z">
                  <div className="text-9xl mb-4" data-id="element-3701" data-mp="1nbowpvx9decc">🤖</div>
                  <p className="text-2xl font-bold text-purple-700" data-id="element-3702" data-mp="1s59v3p2rkac2">
                    Click me!
                  </p>
                </motion.div>
              </div>

              <div className="mt-8 text-center" data-id="element-3703" data-mp="1ie5hasotqvit">
                <p className="text-xl text-gray-600 mb-4" data-id="element-3704" data-mp="1ieoqnl0esbl8">
                  This is just a tiny preview! In the real program, you'll build
                  way cooler stuff!
                </p>
                <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="px-8 py-4 rounded-full font-black text-xl text-white" style={{
                backgroundColor: '#FF6B6B'
              }} data-id="element-3705" data-mp="2b2kmkni5lzgy">
                  Start Building Now! 🚀
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20" style={{
      backgroundColor: '#FFFFFF'
    }} data-id="element-3706" data-mp="15xxrcrmak4kd">
        <div className="container mx-auto px-4" data-id="element-3707" data-mp="115ns2kzads5f">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3708" data-mp="114uvudxyel1j">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#FF6B6B'
          }} data-id="element-3709" data-mp="r65ecm9yz2lt">
              Kids Love It! ⭐⭐⭐⭐⭐
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3710" data-mp="1jx6uoqpef46x">
              See what other kids have built!
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-3711" data-mp="1avz835w8vrso">
            {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} whileHover={{
            y: -10
          }} className="bg-gradient-to-br from-yellow-100 to-orange-100 rounded-3xl p-6 shadow-xl" data-id="element-3712" data-mp="3ix6bwl5pfg3" data-mpi={index}>
                <div className="flex items-center gap-1 mb-4" data-id="element-3713" data-mp="2k4lot6oykrvt">
                  {[...Array(5)].map((_, i) => <Star key={i} size={24} fill="#FFD700" color="#FFD700" data-id="element-3714" data-mp="2ow3olrf8wmf7" data-mpi={i} />)}
                </div>
                <div className="mb-4" data-id="element-3715" data-mp="2tv6n0t0pzue7">
                  <img src={testimonial.image} alt={testimonial.name} className="w-24 h-24 rounded-full border-4 border-white shadow-lg mx-auto" data-id="element-3716" data-mp="2tu5d4rvfxdy2" />
                </div>
                <p className="text-xl font-bold mb-2 text-purple-900" data-id="element-3717" data-mp="32v3gr91yvjg4">
                  "{testimonial.quote}"
                </p>
                <p className="text-lg font-black text-purple-700" data-id="element-3718" data-mp="37twlod36u24o">
                  - {testimonial.name}
                </p>
                <div className="mt-4 bg-white rounded-full px-4 py-2 inline-block" data-id="element-3719" data-mp="18joq8q14tfzb">
                  <p className="text-sm font-bold text-gray-700" data-id="element-3720" data-mp="1dihstnevrmkp">
                    Built: {testimonial.project}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Parent FAQs */}
      <section className="py-20" style={{
      backgroundColor: '#F0F4FF'
    }} data-id="element-3721" data-mp="2ps97bvsj5d2z">
        <div className="container mx-auto px-4" data-id="element-3722" data-mp="2ksej600531lm">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-3723" data-mp="2krl6kiei1hvk">
            <h2 className="text-5xl md:text-6xl font-black mb-4" style={{
            color: '#764BA2'
          }} data-id="element-3724" data-mp="2up9ds7takhum">
              Questions? We've Got Answers! 💬
            </h2>
            <p className="text-2xl text-gray-600" data-id="element-3725" data-mp="34houz2wrl2eg">
              Everything parents want to know
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4" data-id="element-3726" data-mp="3dhtuyrzmoyil">
            {faqs.map((faq, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl shadow-lg overflow-hidden" data-id="element-3727" data-mp="3dfqwdjketx29" data-mpi={index}>
                <button onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)} className="w-full p-6 flex items-center justify-between hover:bg-purple-50 transition-colors" data-id="element-3728" data-mp="3s391gj1vwtp7">
                  <span className="text-xl font-bold text-left text-purple-900" data-id="element-3729" data-mp="3j3uc5mxgep6a">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: expandedFAQ === index ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-3730" data-mp="34gj2jqku1f8t">
                    <ChevronDown size={28} color="#764BA2" data-id="element-3731" data-mp="2vfb3v7eemce7" />
                  </motion.div>
                </button>
                <motion.div initial={false} animate={{
              height: expandedFAQ === index ? 'auto' : 0,
              opacity: expandedFAQ === index ? 1 : 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-3732" data-mp="2zf3ia0qhbcrh">
                  <div className="p-6 pt-0 text-lg text-gray-700" data-id="element-3733" data-mp="35w56p2eez93f">
                    {faq.answer}
                  </div>
                </motion.div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20" style={{
      background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)'
    }} data-id="element-3734" data-mp="2wycqlw0vbxez">
        <div className="container mx-auto px-4" data-id="element-3735" data-mp="2n6ghlpx8umvz">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="max-w-4xl mx-auto text-center" data-id="element-3736" data-mp="2n78er56hosod">
            <h2 className="text-5xl md:text-7xl font-black mb-6 text-white" data-id="element-3737" data-mp="2da3r10wzwp5g">
              Ready to Start? 🎉
            </h2>
            <p className="text-3xl mb-8 text-white font-bold" data-id="element-3738" data-mp="op69spo8khy4">
              Join 500+ kids already building with AI!
            </p>

            <div className="bg-white rounded-3xl p-8 mb-8" data-id="element-3739" data-mp="sonevhzr3d2r">
              <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-3740" data-mp="xotdji4ftzuc">
                <div data-id="element-3741" data-mp="xn8kjwxruoyl">
                  <div className="text-5xl font-black text-purple-700" data-id="element-3742" data-mp="12o5ytyfnuyut">
                    ₦50,000
                  </div>
                  <div className="text-xl text-gray-600" data-id="element-3743" data-mp="51fdvc9cuq6j">4-Week Program</div>
                </div>
                <div data-id="element-3744" data-mp="9yz3177lcnd9">
                  <div className="text-5xl font-black text-purple-700" data-id="element-3745" data-mp="a0a3ax1tvczm">16</div>
                  <div className="text-xl text-gray-600" data-id="element-3746" data-mp="exsfqxf20acd">Live Sessions</div>
                </div>
                <div data-id="element-3747" data-mp="jptqug0hjv0t">
                  <div className="text-5xl font-black text-purple-700" data-id="element-3748" data-mp="jr4r45uq2kn6">∞</div>
                  <div className="text-xl text-gray-600" data-id="element-3749" data-mp="3ieiqfplkzbke">Fun & Learning!</div>
                </div>
              </div>
            </div>

            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="px-16 py-6 rounded-full font-black text-3xl shadow-2xl" style={{
            backgroundColor: '#FFE66D',
            color: '#764BA2'
          }} data-id="element-3750" data-mp="3ncsivsfx7y2b">
              🚀 Enroll Your Child Now!
            </motion.button>

            <p className="text-white text-xl mt-6" data-id="element-3751" data-mp="3a9qf4384jv4t">
              Limited spots available • Next cohort starts soon!
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
};